import React, { useEffect } from "react";
import { useApp } from "./AppContext";
import { useAppRouter } from "../hooks/useAppRouter";
import { AuthFlow } from "./AuthFlow";
import { TaskDashboard } from "./TaskDashboard";
import { ContractData } from "./ContractData";
import { EventApplication } from "./EventApplication";
import { CodeOfConduct } from "./CodeOfConduct";
import { MyProfile } from "./MyProfile";
import { ProfileData } from "./ProfileData";
import { FinancialData } from "./FinancialData";
import { BillingData } from "./BillingData";
import { PayrollDocument } from "./PayrollDocument";
import { JobDetail } from "./JobDetail";
import { TaskList } from "./TaskList";
import { Settings } from "./Settings";
import { Appointments } from "./Appointments";
import { CheckInList } from "./CheckInList";
import { CheckOutList } from "./CheckOutList";
import { Apply } from "./Apply";
import { WebinarBooking } from "./WebinarBooking";
import { TrainingBooking } from "./TrainingBooking";
import { IDTerms } from "./IDTerms";
import { IDCapture } from "./IDCapture";
import { IDResult } from "./IDResult";
import { ProfileCompletion } from "./ProfileCompletion";
import { OccupationProof } from "./OccupationProof";
import { WorkPermitProof } from "./WorkPermitProof";
import { PayrollData } from "./PayrollData";
import { PayrollInfo } from "../screens/PayrollInfo";
import { EventDetails } from "../screens/EventDetails";
import { GroupSelection } from "../screens/GroupSelection";
import { GroupCreate } from "../screens/GroupCreate";
import { GroupJoin } from "../screens/GroupJoin";
import { GroupRouter } from "./GroupRouter";

import { ContractManagement } from "../screens/ContractManagement";
import { InfoFAQ } from "../screens/InfoFAQ";
import { AppSettings } from "../screens/AppSettings";
import { CheckIn } from "../screens/CheckIn";
import { CheckOut } from "../screens/CheckOut";
import { Notifications } from "../screens/Notifications";
import { ProfileIntro } from "../screens/ProfileIntro";
import { ProfileCompletionOverview } from "../screens/ProfileCompletionOverview";
import { OnboardingWelcome } from "../screens/OnboardingWelcome";
import { OnboardingStep } from "../screens/OnboardingStep";
import { ContractDataIntro } from "../screens/ContractDataIntro";
import { EventApplicationIntro } from "../screens/EventApplicationIntro";
import { CodeOfConductIntro } from "../screens/CodeOfConductIntro";
import { PayrollInfoIntro } from "../screens/PayrollInfoIntro";
import { IDCheckIntro } from "../screens/IDCheckIntro";
import { CombinedEventDashboard } from "./CombinedEventDashboard";
import { DeleteMyAccount } from "../screens/DeleteMyAccount";
import { PrmData } from "../screens/PrmData";

export function MainApp() {
	const appContext = useApp();
	const router = useAppRouter(); // Initialize router for URL syncing
	const {
		currentScreen,
		isAuthenticated,
		idCheckState,
		setCurrentScreen,
		canAccessScreen,
		checkFirstTimeOnboarding,
		isFirstTimeUser,
	} = appContext;

	// Check for first-time onboarding after authentication
	// Must be called before any conditional returns to follow React's Rules of Hooks
	useEffect(() => {
		if (isAuthenticated && isFirstTimeUser() && currentScreen === "dashboard") {
			checkFirstTimeOnboarding();
		}
	}, [
		isAuthenticated,
		currentScreen,
		isFirstTimeUser,
		checkFirstTimeOnboarding,
	]);

	// For authenticated users, check access but be more permissive for flexible screens
	const accessCheck = isAuthenticated
		? canAccessScreen(currentScreen)
		: { allowed: true, redirectTo: null };

	// Handle screen redirects in useEffect to avoid setState during render
	// This hook MUST be called before any conditional returns to follow React's Rules of Hooks
	useEffect(() => {
		if (
			isAuthenticated &&
			!accessCheck.allowed &&
			accessCheck.redirectTo &&
			![
				"intro-profile-completion",
				"intro-contract-data",
				"intro-event-application",
				"intro-code-of-conduct",
				"intro-payroll-info",
				"intro-id-check",
				"app-profile-completion",
				"app-event-application",
				"app-payroll-info",
				"app-group-select",
				"app-group-create",
				"app-group-join",
				"app-group",
			].includes(currentScreen)
		) {
			// Only redirect for non-flexible screens
			setCurrentScreen(accessCheck.redirectTo);
		}
	}, [
		isAuthenticated,
		accessCheck.allowed,
		accessCheck.redirectTo,
		currentScreen,
		setCurrentScreen,
	]);

	// If not authenticated, show auth flow
	if (!isAuthenticated) {
		return <AuthFlow />;
	}

	if (!accessCheck.allowed && accessCheck.redirectTo) {
		return null; // Return null while redirecting
	}

	const renderScreen = () => {
		switch (currentScreen) {
			// Authentication screens (should not be accessible when authenticated)
			case "auth-sign-in":
			case "auth-create-account":
			case "auth-verify-email":
			case "auth-phone-number":
			case "auth-verify-phone":
			case "auth-setup-password":
			case "auth-complete-profile":
				// Post-signup: redirect to dashboard for user choice
				setCurrentScreen("dashboard");
				return <TaskDashboard />;

			// Main dashboard - now home/summary screen for user choice
			case "dashboard":
				return <TaskDashboard />;

			// My Event screen - the actual event details screen with combined switcher
			case "my-event":
				return <CombinedEventDashboard />;

			// Onboarding intro screens - Note: Profile intro is now handled as overlay in ProfileCompletion
			case "intro-profile-completion":
				// Redirect to actual profile completion screen with overlay
				setCurrentScreen("app-profile-completion");
				return <ProfileCompletion />;
			case "intro-contract-data":
				return <ContractDataIntro />;
			case "intro-event-application":
				return <EventApplicationIntro />;
			case "intro-code-of-conduct":
				return <CodeOfConductIntro />;
			case "intro-payroll-info":
				return <PayrollInfoIntro />;
			case "intro-id-check":
				return <IDCheckIntro />;

			// Core application screens - allow more flexible access
			case "app-profile-completion":
				return <ProfileCompletion />;
			case "occupation-proof":
				return <OccupationProof />;
			case "work-permit-proof":
				return <WorkPermitProof />;
			case "app-profile-completion-overview":
				return <ProfileCompletionOverview />;
			case "app-payroll-data":
				return <PayrollData />;
			case "app-prm-data":
				return <PrmData />;
			case "app-contract-data":
				return <ContractData />;
			case "app-event-application":
				return <EventApplication />;
			case "app-code-of-conduct":
				return <CodeOfConduct />;
			case "app-payroll-info":
				return <PayrollInfo />;
			case "app-event-details-1":
				return <EventDetails />;
			case "app-event-details-2":
				return <EventDetails />;
			case "app-group-select":
				return <GroupSelection />;
			case "app-group-create":
				return <GroupCreate />;
			case "app-group-join":
				return <GroupJoin />;
			case "app-group":
				return <GroupRouter />;
			case "app-notifications":
				return <Notifications />;
			case "app-contract":
				return <ContractManagement />;
			case "app-info-faq":
				return <InfoFAQ />;
			case "app-settings":
				return <AppSettings />;

			// Onboarding flow screens
			case "onboarding-welcome":
				return <OnboardingWelcome />;
			case "onboarding-step-profile":
				return <OnboardingStep stepNumber={1} />;
			case "onboarding-step-code-of-conduct":
				return <OnboardingStep stepNumber={2} />;
			case "onboarding-step-event-application":
				return <OnboardingStep stepNumber={3} />;
			// Removed steps - kept for backwards compatibility but redirect to dashboard
			case "onboarding-step-contract-data":
			case "onboarding-step-payroll-info":
			case "onboarding-step-id-check":
				setCurrentScreen("dashboard");
				return <TaskDashboard />;

			// Operational screens
			case "operations-check-in":
				return <CheckIn />;
			case "operations-check-out":
				return <CheckOut />;

			// User account management
			case "delete-my-account":
				return <DeleteMyAccount />;

			// Legacy screens (maintain compatibility)
			case "my-profile":
				return <MyProfile />;
			case "profile-data":
				return <ProfileData />;
			case "financial-data":
				return <FinancialData />;
			case "billing-data":
				return <BillingData />;
			case "payroll-document":
				return <PayrollDocument />;
			case "job-detail":
				return <JobDetail />;
			case "task-list":
				return <TaskList />;
			case "settings":
				return <Settings />;
			case "notifications":
				return <Notifications />;
			// Appointments functionality hidden - preserve for future use
			case "appointments":
			case "webinar-booking":
			case "training-booking":
				// Redirect to dashboard if someone tries to access directly
				setCurrentScreen("dashboard");
				return <TaskDashboard />;
			case "check-in-list":
				return <CheckInList />;
			case "check-out-list":
				return <CheckOutList />;
			case "apply":
				return <Apply />;
			case "id-terms":
				// Only show terms if status is Pending
				if (idCheckState.status === "Pending") {
					return <IDTerms />;
				} else {
					// Redirect to result if already completed
					setCurrentScreen("id-result");
					return <IDResult />;
				}
			case "id-capture":
				// Allow access for both initial and replacement flows
				if (
					idCheckState.status === "Pending" ||
					idCheckState.navigationContext === "replacement"
				) {
					return <IDCapture />;
				} else {
					setCurrentScreen("id-result");
					return <IDResult />;
				}
			case "id-result":
				return <IDResult />;
			default:
				// Default route: authenticated users go to dashboard (home/summary screen)
				return <TaskDashboard />;
		}
	};

	return renderScreen();
}
